/**
 * @lodex-pro/web-portal/public/publicApi — bare fetch wrapper for the public
 * unauthenticated portal sign-up endpoints (DEC-155 / Phase 15 followup).
 *
 * IMPORTANT: Does NOT use `authedFetch` from @lodex-pro/web-shared:
 *   (a) authedFetch reads Amplify session → returns null for signed-out users
 *       but still burns cycles;
 *   (b) authedFetch sets credentials: 'include' which sends cookies the API
 *       doesn't want on public routes;
 *   (c) authedFetch has 401→refresh retry logic meaningless for unauthed routes.
 *
 * Uses bare `fetch()` with `VITE_API_BASE_URL` env var.
 *
 * Endpoints: the disposal-portal sign-up routes. The operator ones were removed with operator
 * self-signup (operator portal rework PR 1); Paelode creates operator accounts.
 *
 * Spec: DEC-148, DEC-149, DEC-154, DEC-155.
 */

import type {
  DisposalRegistrationRequest,
  DisposalRegistrationResponse,
  DisposalMagicLinkIssueRequest,
  DisposalMagicLinkConsumeRequest,
  DisposalMagicLinkConsumeResponse,
} from '@lodex-pro/shared/validation/disposal-portal';

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export interface PublicApiErrorInit {
  status: number;
  code?: string | undefined;
  message?: string | undefined;
  retryAfterSeconds?: number | undefined;
  fieldErrors?: Record<string, string> | undefined;
}

/**
 * Thrown by publicPost on non-2xx responses or network failures.
 * Callers can typeguard with `err instanceof PublicApiError`.
 */
export class PublicApiError extends Error {
  readonly status: number;
  /** ERROR_CATALOG code, e.g. 'CAPTCHA_REQUIRED', 'RATE_LIMIT_EXCEEDED' */
  readonly code: string | undefined;
  /** Retry-After parsed as seconds (only on 429) */
  readonly retryAfterSeconds: number | undefined;
  /** Field-level errors from VALIDATION_FAILED, keyed by field path */
  readonly fieldErrors: Record<string, string> | undefined;

  constructor(init: PublicApiErrorInit) {
    super(init.message ?? `HTTP ${init.status}`);
    this.name = 'PublicApiError';
    this.status = init.status;
    this.code = init.code;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.fieldErrors = init.fieldErrors;
  }
}

// ---------------------------------------------------------------------------
// Core helper
// ---------------------------------------------------------------------------

/**
 * POST `path` with JSON `body`. Returns the parsed JSON response on 2xx.
 * Throws `PublicApiError` on non-2xx or network failure.
 *
 * `baseUrl` is read from `import.meta.env.VITE_API_BASE_URL` at call-time
 * (NOT at module load) so that Vite's build replacement and test stubEnv both
 * work correctly.
 */
export async function publicPost<TBody, TResponse>(path: string, body: TBody): Promise<TResponse> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  let res: Response;
  try {
    res = await fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      // Deliberately no credentials:'include' — public endpoints; no cookies needed.
    });
  } catch (_err) {
    // Network failure (fetch rejected — no response)
    throw new PublicApiError({ status: 0, message: 'Network error. Check your connection.' });
  }

  if (res.ok) {
    // Handle empty body (202 from magic-link/issue)
    const contentLength = res.headers.get('content-length');
    if (contentLength === '0' || contentLength === null) {
      // Try to parse anyway; if it throws, return undefined
      try {
        return (await res.json()) as TResponse;
      } catch {
        return undefined as unknown as TResponse;
      }
    }
    return (await res.json()) as TResponse;
  }

  // Non-2xx: parse the error envelope
  let errorBody: {
    error?: { code?: string; message?: string; fields?: Array<{ path: string; reason: string }> };
  } = {};
  try {
    errorBody = (await res.json()) as typeof errorBody;
  } catch {
    // Body not parseable — use generic error
  }

  const code = errorBody.error?.code;
  const message = errorBody.error?.message;

  // Parse Retry-After header on 429 (numeric seconds; skip RFC 7231 date format)
  let retryAfterSeconds: number | undefined;
  if (res.status === 429) {
    const retryAfter = res.headers.get('retry-after');
    if (retryAfter !== null) {
      const parsed = parseInt(retryAfter, 10);
      if (!Number.isNaN(parsed)) {
        retryAfterSeconds = parsed;
      }
    }
  }

  // Parse field-level errors from VALIDATION_FAILED
  // API shape: error.fields = [{ path: 'body.email', reason: 'invalid_string' }]
  // Flatten to { 'email': 'Invalid email address' } for react-hook-form setError
  let fieldErrors: Record<string, string> | undefined;
  if (code === 'VALIDATION_FAILED' && Array.isArray(errorBody.error?.fields)) {
    fieldErrors = {};
    for (const field of errorBody.error.fields) {
      // Strip 'body.' prefix if present (Fastify adds it for body validation)
      const fieldName = field.path.replace(/^body\./, '');
      fieldErrors[fieldName] = humanizeZodReason(field.reason, fieldName);
    }
  }

  throw new PublicApiError({
    status: res.status,
    code,
    message,
    retryAfterSeconds,
    fieldErrors,
  });
}

/** Convert a Zod reason code to a human-readable message. */
function humanizeZodReason(reason: string, fieldName: string): string {
  switch (reason) {
    case 'too_small':
      return `${fieldName} is too short.`;
    case 'too_big':
      return `${fieldName} is too long.`;
    case 'invalid_string':
      return `${fieldName} format is invalid.`;
    case 'invalid_type':
      return `${fieldName} is required.`;
    default:
      return `${fieldName} is invalid.`;
  }
}

// ---------------------------------------------------------------------------
// Disposal-portal named convenience wrappers (DEC-160)
//
// They mirrored the operator functions, removed with operator self-signup. Backend endpoints shipped in
// Phase 16 / Task 16.1 (DEC-156). See `disposal-portal.ts` in `@lodex-pro/shared`
// for the exact request/response shapes.
// ---------------------------------------------------------------------------

/**
 * POST /v1/disposal-portal/register
 * Returns { registrationId, licenseMatchStatus } on 201.
 */
export async function disposalRegister(
  body: DisposalRegistrationRequest,
): Promise<DisposalRegistrationResponse> {
  return publicPost<DisposalRegistrationRequest, DisposalRegistrationResponse>(
    '/v1/disposal-portal/register',
    body,
  );
}

/**
 * POST /v1/disposal-portal/magic-link/issue
 * Returns void (202 empty body).
 */
export async function disposalMagicLinkIssue(body: DisposalMagicLinkIssueRequest): Promise<void> {
  await publicPost<DisposalMagicLinkIssueRequest, void>(
    '/v1/disposal-portal/magic-link/issue',
    body,
  );
}

/**
 * POST /v1/disposal-portal/magic-link/consume
 * Returns { registrationId, email } on 200.
 * NOTE: captchaToken NOT required (removed per DEC-155 grey area 2; DEC-156
 * kept the same posture for disposal).
 */
export async function disposalMagicLinkConsume(
  body: DisposalMagicLinkConsumeRequest,
): Promise<DisposalMagicLinkConsumeResponse> {
  return publicPost<DisposalMagicLinkConsumeRequest, DisposalMagicLinkConsumeResponse>(
    '/v1/disposal-portal/magic-link/consume',
    body,
  );
}
